import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail, setProductionId, getProductionId } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Auth provider with a "View as Org Admin" impersonation layer.
 *
 * When the signed-in user is super_admin and toggles `viewAsOrgAdmin` on,
 * every downstream consumer that reads `user.role` sees `"org_admin"`. This
 * gives support/onboarding staff a byte-for-byte view of what the actual
 * client sees inside a production — no code changes needed at call sites.
 *
 * Two escape hatches for the few places that MUST know the truth:
 *   • `isRealSuperAdmin`  — bool, ignores the toggle. Used by the toggle UI,
 *                            the "you're viewing as…" banner, and the
 *                            Platform Admin route guard.
 *   • `rawUser`           — untouched user object; use very sparingly.
 */
export function AuthProvider({ children }) {
  const [rawUser, setUser] = useState(null); // null=loading, false=guest, obj=authed
  const [error, setError] = useState("");
  const [productionId, setPid] = useState(getProductionId());
  const [production, setProduction] = useState(null);
  const [viewAsOrgAdmin, setViewAsOrgAdminState] = useState(
    () => localStorage.getItem("sc_view_as") === "org_admin"
  );

  const isRealSuperAdmin = rawUser?.role === "super_admin";

  const user = useMemo(() => {
    if (!rawUser || !isRealSuperAdmin || !viewAsOrgAdmin) return rawUser;
    // Byte-for-byte org_admin experience — everything gated on user.role
    // downstream flips automatically. We tag the object so the banner
    // component can detect "we're impersonating" without prop-drilling.
    return { ...rawUser, role: "org_admin", _impersonating: true };
  }, [rawUser, isRealSuperAdmin, viewAsOrgAdmin]);

  const setViewAsOrgAdmin = (on) => {
    setViewAsOrgAdminState(on);
    if (on) localStorage.setItem("sc_view_as", "org_admin");
    else localStorage.removeItem("sc_view_as");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const impersonateUser = async (userId) => {
    // Stash the original super_admin token so we can restore it locally if
    // the backend stop-endpoint is ever unreachable. Backend also encodes
    // the impersonator id in the new JWT — that's the source of truth.
    const original = localStorage.getItem("sc_token");
    if (original) localStorage.setItem("sc_original_token", original);
    const { data } = await api.post(`/platform/impersonate/${userId}`);
    if (data?.token) localStorage.setItem("sc_token", data.token);
    // Wipe any pre-existing production selection & view-as toggle — those
    // belong to the super_admin, not to the impersonated user.
    setViewAsOrgAdmin(false);
    setProductionId(null); setPid(null); setProduction(null);
    await load();
    return data.user;
  };

  const stopImpersonating = async () => {
    try {
      const { data } = await api.post("/platform/stop-impersonation");
      if (data?.token) localStorage.setItem("sc_token", data.token);
    } catch {
      // Fallback to the stashed original if the endpoint fails.
      const stashed = localStorage.getItem("sc_original_token");
      if (stashed) localStorage.setItem("sc_token", stashed);
    }
    localStorage.removeItem("sc_original_token");
    setProductionId(null); setPid(null); setProduction(null);
    await load();
  };

  useEffect(() => {
    if (!productionId || !user) { setProduction(null); return; }
    api.get(`/productions/${productionId}`).then((r) => {
      // Archived shows are only enterable by admins. Compare against the
      // effective role so a super_admin who's impersonating still gets
      // kicked out of an archived show (matches what the org_admin sees).
      if (r.data?.status === "archived" && user?.role !== "org_admin" && user?.role !== "super_admin") {
        setProductionId(null); setPid(null); setProduction(null);
      } else {
        setProduction(r.data);
      }
    }).catch(() => {
      setProductionId(null); setPid(null); setProduction(null);
    });
  }, [productionId, user]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("sc_token", data.token);
      setUser(data.user);
      // Fresh login resets impersonation — you should never inherit view-as
      // state from a previous session.
      setViewAsOrgAdmin(false);
      setProductionId(null); setPid(null); setProduction(null);
      return data.user;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return null;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* logout error is non-fatal */ }
    localStorage.removeItem("sc_token");
    setViewAsOrgAdmin(false);
    setProductionId(null); setPid(null); setProduction(null);
    setUser(false);
  };

  const selectProduction = (id) => { setProductionId(id); setPid(id); };

  return (
    <AuthContext.Provider value={{
      user, rawUser, isRealSuperAdmin, viewAsOrgAdmin, setViewAsOrgAdmin,
      error, login, logout, refresh: load, setError,
      productionId, production, selectProduction,
      impersonateUser, stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
